/* eslint-disable no-magic-numbers, curly, indent, no-unused-expressions, max-lines */

import { traverse } from 'utils/helpers';
import { debounce } from 'utils/debounce';
import ResizeTracker from 'utils/ResizeTracker';
import { scrollUnlock } from 'ui/scrollLock';
import scrollTo from 'ui/scrollTo';

/**
 * An autocomplete input that fetches a list of items and renders them into an expanding list.
 * Selecting an item implicitly redirects to the item Url property.
 * @module FormAutocomplete
 */
class FormAutocomplete {

    /**
     * Class constructor
     * @param {HTMLElement} element - Host element
     * @param {HTMLElement} options - the base options
     */
    constructor(element, options) {
        this._options = { ...options };
        this._element = element;
        this.className = 'form-autocomplete';

        this._state = {
            scrollPosition: 0,
            mobileSize: 768,
            isMobile: false,
            token: null,
            response: null,
            defaultResponse: null,
            isLocal: false,
            filteredResults: [],
            headerHeight: 0,
            fullResults: [],
            isTabbing: false,
            active: false,
            isDebug: this._options.debug === 'true'
        };

        this._cacheSelectors();
        this._setLiveRegionOptions();
        this._setLocal();
        this._setToken();
        this._addEventListeners();
        this._options.fetchResults = this._options.fetchResults === 'true';
        !this._options.fetchResults && this._getDefaultResponse();
        this._options.fetchResults && this._searchQueryHandler();

    }

    /**
     * @private
     */
    _cacheSelectors() {
        this._rootContainer = document.body;
        this._input = this._element.querySelector('[data-input]');
        this._dropdown = this._element.querySelector('[data-dropdown]');
        this._container = document.body;
        this._submitButtons = [...this._element.querySelectorAll('[data-submit]')];
        this._searchContent = [...document.querySelectorAll('[data-search-content]')];
        this._defaultContainer = document.querySelector('[data-default-container]');
        this._resultContainer = document.querySelector('[data-result-container]');
        this._resultList = document.querySelector('[data-result-list]');
        this._clearResultsButtons = [...document.querySelectorAll('[data-clear-results]')];
        this._queryLabels = [...document.querySelectorAll('[data-query]')];
        this._close = document.querySelector('[data-close-autocomplete]');
        this._empty = this._element.querySelector('[data-empty-autocomplete]');
        this._token = this._element.querySelector('[name="csrfToken"]');
        this._lastSection = [...document.querySelectorAll('main section:last-of-type a,main section:last-of-type button')];
        this._mobileButtons = [...this._element.querySelectorAll('[data-mobile]')];
        this._direction = 'down';
        this._header = document.querySelector('.header');
        this._questions = [...document.querySelectorAll('.search-results__question,.search-categories__question')];
        this._resultsError = document.querySelector('[data-results-error]');
        this._resultsEmpty = document.querySelector('[data-results-empty]');
        this._ctaLink = this._element.querySelector('.form-autocomplete__cta');
        this._ctaPreviewLink = this._element.querySelector('.form-autocomplete__cta-preview');
        this._liveRegion = {
            container: document.querySelector(`#${this._options.liveRegion}`),
            counter: document.querySelector(`#${this._options.liveRegion} .counter`),
            tag: document.querySelector(`#${this._options.liveRegion} .tag`),
            prefix: document.querySelector(`#${this._options.liveRegion} .prefix`)
        };

    }

    _setLocal() {
        const hostname = window.location.hostname;
        this._state.isLocal = this._state.isDebug ? hostname.includes('local') || hostname.includes('frontend') : false;
    }

    /**
     * @private
     */
    _addEventListeners() {
        document.addEventListener('click', event => this._onClickDocument(event));
        this._element.addEventListener('click', () => this._activate());
        this._element.addEventListener('focus', () => this._activate());
        this._input.addEventListener('focus', () => this._activate());
        this._element.addEventListener('blur', () => this._deactivate());
        this._element.addEventListener('keydown', event => this._onKeydownElement(event));
        this._empty.addEventListener('click', event => this._emptyForm(event));

        this._submitButtons.forEach(button => {
            button.addEventListener('click', event => this._handleSubmit(event));
        });

        this._clearResultsButtons && this._clearResultsButtons.forEach(button => {
            button.addEventListener('click', event => this._handleClearResults(event));
        });

        this._questions && this._questions.forEach(question => {
            question.addEventListener('click', event => this._handleQuestionClicked(event));
        });

        this._input.addEventListener('input', debounce(() => {
            this._onInput();
        }, 300));

        this._input.addEventListener('keydown', event => this._handleTabbedInput(event));

        document.addEventListener('keydown', event => this._handleTabbedNavigation(event));

        if (!this._state.mobileSize) return;
        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: this._rootContainer },
            this._setIsMobile
        );

        this._setIsMobile();

        this._options.fetchResults && this._hashLoadHandler(null);

    }


    /**
     * Set mobile state
     * @private
     */
    _setIsMobile() {
        if (!this._rootContainer) return;
        const isMobile = this._rootContainer.clientWidth < this._state.mobileSize;
        const em = parseFloat(getComputedStyle(this._header).fontSize) * 4;
        // eslint-disable-next-line no-extra-parens
        this._state.headerHeight = isMobile ? this._header.offsetHeight + (em * 2) : this._header.offsetHeight + em;
        this._state.isMobile = isMobile;
        this._mobileButtons && this._mobileButtons.forEach(button => {
            button.setAttribute('aria-hidden', !this._state.isMobile);
        });
    }

    /**
     * Behaviour specific to either clicking on the host element or bringing it
     * into focus
     */
    _activate() {
        if (this._state.active) return;
        this._state.active = true;
        this._state.scrollPosition = window.scrollY || window.scrollTop || window.pageYOffset;
        !this._state.isMobile && !this._options.fetchResults && this._options.type !== 'section' && this._container.setAttribute('data-search-active', true);
        this._element.setAttribute('data-is-active', '');
        this._input.focus();
        this._element.setAttribute('data-is-focused', '');
        this._options.apiDefaultUrl && this._onInput();
        this._state.isMobile && this._options.type !== 'section' && !this._options.fetchResults && this._lockScroll();
    }

    /**
     * Prevent the body from scrolling
     */
    _lockScroll() {
        this._container.setAttribute('data-search-active', true);
        scrollTo(null, 0);
        const timeout = setTimeout(() => {
            document.documentElement.setAttribute('data-locked', '');
            scrollTo(null, 0, 'smooth', 'vertical', this._element);
            this._container.style.width = '100vw';
            this._container.style.width = '100vw';
            clearTimeout(timeout);
        }, 600);

    }

    _searchQueryHandler() {
        const search = this._options.searchQuery;
        const isInvalid = !search || search.length < 2;
        this._resultContainer.setAttribute('aria-hidden', isInvalid);
        if (isInvalid) return;
        this._input.value = search.toLowerCase();
        this._fetchResults();
    }

    /**
     * Get the hash on page load
     * @param {string} hash
     */
    _hashLoadHandler(hash) {
        this._options.fetchResults && window.scrollTo(0, 0);
        // Fix for safari
        const timeout = this._options.fetchResults && setTimeout(() => {
            window.scrollTo(0, 0);
            clearTimeout(timeout);
        }, 2);
        const target = hash ? hash : window.location.hash && window.location.hash.substr(1);
        if (!target) return;
        const targetHash = target.replace('result-', '');
        let sectionToOpen = null;
        this._searchContent && this._searchContent.forEach(section => {
            const targetContent = section.querySelector(`#${targetHash}`);
            if (!targetContent) return;
            sectionToOpen = targetContent;
        });
        sectionToOpen && this._openSection(sectionToOpen);
    }

    // eslint-disable-next-line class-methods-use-this
    _clearHashFromLocation() {
        window.history.pushState('', document.title, window.location.pathname);
    }

    /**
     * Open section and scroll to the section
     * @param {element} section
     */
    _openSection(section) {
        const container = section.closest('.search-categories__section');
        const questionList = container.querySelector('.search-categories__questions-list');
        const questionListItem = section.closest('[data-collapsible]');
        questionListItem.setAttribute('data-is-open', true);
        const activeQuestions = [...container.closest('.search-categories__questions-col').querySelectorAll('section')];
        activeQuestions && activeQuestions.forEach(question => {
            question.setAttribute('aria-hidden', true);
        });
        container.setAttribute('aria-hidden', false);
        const questionType = questionList.getAttribute('data-type');
        const categoryTarget = document.querySelector(`[data-target="#${questionType}"]`);
        categoryTarget.classList.add('active');
        const categoryTargetLink = categoryTarget.querySelector('a');
        categoryTargetLink.setAttribute('aria-selected', true);
        categoryTarget.closest('section').setAttribute('data-filtered', true);
        questionList.setAttribute('aria-hidden', false);
        questionList.previousElementSibling.setAttribute('aria-expanded', true);
        section.setAttribute('aria-hidden', false);
        section.previousElementSibling.setAttribute('aria-expanded', true);
        const timeoffset = 1200;
        const timeout = setTimeout(() => {
            this._scrollToElement(section);
            clearTimeout(timeout);
        }, timeoffset);
    }


    /**
     * Scrolls to clicked question after tiemout
     * @param {event} event
     */
    _handleQuestionClicked(event) {
        event.preventDefault();
        const target = event.target;
        const timeout = setTimeout(() => {
            this._scrollToElement(target);
            clearTimeout(timeout);
        }, 800);
    }


    /**
     * Behaviour specific to either clicking outside of the host element or
     * generically blurring it.
     */
    _deactivate() {
        this._state.active = false;
        this._container.setAttribute('data-search-active', false);
        this._element.removeAttribute('data-is-active');
        this._element.removeAttribute('data-is-focused');
        this._element.removeAttribute('data-is-open');
        this._element.removeAttribute('data-has-content');
        document.documentElement.removeAttribute('data-locked');
        this._state.isMobile && this._options.type !== 'section' && scrollUnlock();
        this._state.isMobile && this._options.type !== 'section' && window.scrollTo(0, this._state.scrollPosition);
    }

    /**
     * Set the CSRF token
     */
    _setToken() {
        this._state.token = this._token.getAttribute('value');
    }


    /**
     * When typing send a request then call renderList or an error
     */
    _onInput() {
        const inputValue = this._input.value.toLowerCase().trim();
        const hasContent = inputValue.length > 2;
        !hasContent && this._element.removeAttribute('data-has-content');
        // eslint-disable-next-line no-extra-parens
        if ((!hasContent && !this._options.apiDefaultUrl)) {
            this._element.removeAttribute('data-is-focused');
            this._element.removeAttribute('data-is-open');
            this._clear();

            return;
        }

        hasContent && this._element.setAttribute('data-has-content', '');
        !hasContent && !this._options.fetchResults && this._showList();

        this._queryLabels && this._queryLabels.forEach(label => {
            label.innerHTML = ` "${this._input.value}"`;
        });

        if (hasContent) {
            this._ctaPreviewLink && this._ctaPreviewLink.setAttribute('aria-hidden', false);
            this._ctaLink && this._ctaLink.setAttribute('aria-hidden', true);

        } else {
            this._ctaPreviewLink && this._ctaPreviewLink.setAttribute('aria-hidden', true);
            this._ctaLink && this._ctaLink.setAttribute('aria-hidden', false);
        }

        hasContent && this._fetchApi(inputValue, 'suggestions')
            .then(items => {
                this._state.response = items;
                this._renderList(inputValue);
                this._element.setAttribute('data-is-open', '');
            })
            .catch(() => {
                this._renderError('suggestions');
            });

        this._options.fetchResults && this._submitButtons && this._submitButtons.forEach(button => {
            hasContent ? button.removeAttribute('disabled') : button.setAttribute('disabled', '');
        });

    }

    _handleTabbedInput(event) {
        this._state.isTabbing = event.code === 'Tab' ? true : this._state.isTabbing;
        const backFocus = event.keyCode === 9 && event.shiftKey === true;
        backFocus && event.preventDefault();
        backFocus && this._lastSection[this._lastSection.length - 1].focus();
    }

    _handleTabbedNavigation(event) {
        const isTabbing = event.code === 'Tab';
        this._state.isTabbing = isTabbing ? true : this._state.isTabbing;
        isTabbing && document.removeEventListener('keydown', this._handleTabbedNavigation);
    }

    /**
     * Empty the input field
     * @param {MouseEvent} event - Click event
     */
    _emptyForm(event) {
        event && event.preventDefault();
        this._input.value = '';
        this._element.removeAttribute('data-has-content');
        this._state.filteredResults = [];
    }

    /**
     * Show the default list
     */
    _getDefaultResponse() {
        this._fetchApi('', 'suggestions')
            .then(items => {
                this._state.defaultResponse = items;
            })
            .catch(() => {
                this._renderError('suggestions');
            });
    }

    _handleSubmit(event) {
        event.preventDefault();
        if (this._input.value.length < 3) return;
        this._options.fetchResults && this._fetchResults();
        !this._options.fetchResults && this._goToResultPage();
    }

    _goToResultPage() {
        this._element.submit();
    }

    _scrollToElement(scrollTarget) {
        const currentScroll = window.scrollY || window.scrollTop || window.pageYOffset || document.documentElement.scrollTop;
        const targetBoundingBox = scrollTarget.getBoundingClientRect();
        const targetOffset = currentScroll + targetBoundingBox.top;
        const offset = targetOffset - this._state.headerHeight;
        scrollTo(null, offset);
    }

    _handleClearResults(event) {
        event.preventDefault();
        this._resultContainer.setAttribute('aria-hidden', true);
        this._defaultContainer.setAttribute('aria-hidden', false);
        this._resultList.innerHTML = '';
        this._emptyForm(null);
    }

    /**
     * Show the default list
     */
    _showList() {
        this._renderList();
        this._element.setAttribute('data-is-open', '');
    }


    /**
     * Deactivates the dropdown when clicked outside
     * @param {MouseEvent} event - Click event
     */
    _onClickDocument(event) {
        if (!this._element.contains(event.target) && this._element.hasAttribute('data-is-active')) {
            this._deactivate();
        }
    }

    /**
     * Manage keydown navigation
     * @param {KeyboardEvent} event - The keyboard event triggered
     */
    _onKeydownElement(event) {
        if (!this._element.hasAttribute('data-is-open')) return;
        const currentFocusedItem = this._dropdown.querySelector(':focus');
        const items = Array.from(this._dropdown.childNodes);
        switch (event.code) {
            case 'ArrowDown':
                this._direction = 'down';
                items[0].style.marginTop = '2px';
                traverse('down', items, currentFocusedItem);
                break;
            case 'ArrowUp':
                this._direction = 'up';
                items[0].style.marginTop = 0;
                traverse('up', items, currentFocusedItem);
                break;
            case 'Tab':
                this._state.isTabbing = true;
                break;
            case 'Escape':
                this._deactivate();
                break;
            case 'Enter':
                if (!currentFocusedItem) {
                    if (this._input.value.length < 3) return;
                    event.preventDefault();
                    this._options.fetchResults && this._fetchResults();
                    !this._options.fetchResults && this._goToResultPage();
                }
                break;
            // eslint-disable-next-line no-useless-return
            default: return;
        }
    }

    /**
     * Fetches the item list from the server based on the passed query
     * @param {String} query Query string to be passed on to the server
     * @param {String} type Type of data
     * @returns {Promise<any>} Result of the SS fetch operation
     */
    async _fetchApi(query, type) {
        const queryUrl = query === '' ? this._options.apiDefaultUrl : this._options.apiUrl;
        const url = type === 'suggestions' ? queryUrl : this._options.redirectUrl;
        // eslint-disable-next-line init-declarations
        let results;
        const headers = this._state.isLocal ? {} : {
            headers: {
                'Content-Type': 'application/json; charset=utf-8'
            },
            method: 'POST',
            body: JSON.stringify({
                query: query ? query : '',
                csrfToken: this._state.token
            })
        };

        try {

            const response = await fetch(url, headers);
            if (type === 'search-redirect') {
                return true;
            }

            results = type === 'suggestions' ? this._handleJsonResponse(response) : this._handleHtmlResponse(response);

        } catch (error) {
            this._makeErrorMessage(true);
        }


        return results;
    }

    async _handleJsonResponse(response) {
        const responseJson = await response.json();
        this._makeErrorMessage(false);
        return responseJson.Data.slice(0, 3);
    }

    async _handleHtmlResponse(response) {
        const responseHtml = await response.text();
        this._makeErrorMessage(false);
        return responseHtml;
    }

    _fetchResults() {
        const query = this._input.value.toLowerCase();
        this._fetchApi(query, 'search')
            .then(results => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(results, 'text/html');
                this._state.resultsHtml = [...doc.querySelectorAll('.search-results__results-list-item')];
                const tokenInput = doc.querySelector('[name="csrfToken"]');
                const token = tokenInput.getAttribute('value');
                this._token.setAttribute('value', token);
                this._state.token = token;
                this._createResultList();
            })
            .catch(() => {
                this._renderError('search');
            });
    }

    _createResultList() {
        this._resultList.innerHTML = '';
        const hasResults = this._state.resultsHtml && this._state.resultsHtml.length > 0;
        this._resultsEmpty.setAttribute('aria-hidden', hasResults);
        this._resultsError.setAttribute('aria-hidden', true);

        this._queryLabels && this._queryLabels.forEach(label => {
            label.innerHTML = ` "${this._input.value}"`;
        });

        this._state.resultsHtml && this._state.resultsHtml.forEach(item => {
            const trigger = item.querySelector('[data-trigger]');
            trigger.addEventListener('click', event => this._handleQuestionClicked(event));

            const linkText = item.querySelector('[data-trigger] span');
            const textContent = this._makeQueryLetterBold(this._input.value, linkText.textContent || linkText.innerText);
            linkText.innerHTML = '';
            linkText.innerHTML = textContent;
            this._resultList.append(item);
        });

        this._resultContainer.setAttribute('aria-hidden', false);
        this._defaultContainer.setAttribute('aria-hidden', true);
        this._input.value = '';
        window.initializeModules();
        this._element.removeAttribute('data-has-content');
        this._state.filteredResults = [];
        this._state.fullResults = [];
        this._deactivate();
        this._element.removeAttribute('data-is-focused');
        this._element.removeAttribute('data-is-open');
        this._clear();
        this._scrollToElement(this._resultContainer);
        const firstLink = this._resultContainer.querySelector('[role=button]');
        this._state.isTabbing && firstLink.focus();
    }

    /**
     * Makes the letter which corresponds with the query bold
     * @param {String} query - the query that the client typed
     * @param {Object} item - the particular item that holds the information about this result
     * @returns {String} - new string where the matched letters are wrapped in a span
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    _makeQueryLetterBold(query, item) {
        const match = new RegExp(`(${query})`, 'i');
        const str = item.title || item;
        const result = match ? str.replace(match, `<span class="bold">${query}</span>`) : str;
        return result;
    }

    /**
     * Loop through the data and build an element on it
     * Then append it all on a fragment
     * @param {[]} items - the data of all the items that get returned
     * @param {String} query - the query that is being inputted by the client
     * @returns {[]} items - array containing the new document fragments
     * @private
     */
    _makeLinkList(items, query) {

        const fragment = document.createDocumentFragment();
        this._state.filteredResults = [];
        items && items.forEach(item => {
            const link = document.createElement('a');
            link.className = `${this.className}__links`;
            link.href = `${item.url}`;
            link.title = `${item.title}`;
            link.innerHTML = this._makeQueryLetterBold(query, item);
            this._state.filteredResults.push(link.href);
            fragment.appendChild(link);
        });

        return fragment;
    }


    /**
     * Shows an error message when there are no elements to seach in
     * @private
     * @param {boolean} error
     */
    _makeErrorMessage(error) {
        this._element.setAttribute('data-error', error);
    }

    /**
     * Shows an empty message when there are no items found
     * @private
     * @param {boolean} empty
     */
    _makeEmptyMessage(empty) {
        this._element.setAttribute('data-empty', empty);
        this._handleLiveRegionChange(false);
    }

    /**
     * Renders the item list or an empty box notifying that there are no results
     * @param {String} query Query that generated the provided list of items
     */
    _renderList(query) {
        const items = this._state.response ? this._state.response : this._state.defaultResponse;
        this._state.response = null;

        this._dropdown.innerHTML = '';
        const resultList = items && items;

        if (!items || items.length === 0 || !resultList || resultList.length === 0) {
            this._makeEmptyMessage(true);
            return;
        }

        this._makeEmptyMessage(false);
        const results = this._makeLinkList(resultList, query);
        this._dropdown.appendChild(results);
        this._options.fetchResults && this._addListEventListener();
        this._handleLiveRegionChange(true);
    }

    _addListEventListener() {
        const links = [...this._dropdown.querySelectorAll('.form-autocomplete__links')];
        const results = this._options.fetchResults && [...this._resultList.querySelectorAll('.form-autocomplete__links')];
        const allLinks = [
            ...links,
            ...results
        ];

        allLinks.length > 0 && allLinks.forEach(link => {
            const hash = link.getAttribute('href').split('#');

            link.addEventListener('click', () => {
                this._hashLoadHandler(hash[1]);
            });
        });
    }

    /**
     * Render an error
     * @param {string} type
     */
    _renderError(type) {
        this._element.setAttribute('data-error', true);
        this._resultsError && type === 'search' && this._resultsError.setAttribute('aria-hidden', false);
    }

    /**
     * Clear rendered content
     */
    _clear() {
        this._dropdown.innerHTML = '';
    }

    _setLiveRegionOptions() {
        const liveRegionLabels = {
            multiple: this._liveRegion.container.getAttribute('data-multiple'),
            single: this._liveRegion.container.getAttribute('data-single'),
            empty: this._liveRegion.container.getAttribute('data-empty'),
            nosearch: this._liveRegion.container.getAttribute('data-nosearch')
        };

        this._options.liveRegionLabels = liveRegionLabels;
    }

    /**
     * Update the live region for screen readers
     * @param {boolean} hasResults
     */
    _handleLiveRegionChange(hasResults) {
        const counter = hasResults ? this._state.fullResults.length || this._state.filteredResults.length : 0;
        const singleSentence = this._input.value ? this._options.liveRegionLabels.empty : this._options.liveRegionLabels.nosearch;
        const searchSentence = counter > 1 ? this._options.liveRegionLabels.multiple : this._options.liveRegionLabels.single;
        const prefix = counter === 0 ? singleSentence : searchSentence;
        const tag = this._input.value;
        const counterText = counter > 0 ? counter : '';
        this._liveRegion.counter.textContent = `${counterText} ${prefix} ${tag}`;
    }
}

export default FormAutocomplete;

/* eslint-enable */
